import classNames from 'classnames';
import React, { DragEventHandler, MouseEvent, useState, useEffect } from 'react';
import { Nullable } from 'src/types/nullable.type';
import { functionNoop } from 'src/utils/function/noop';

export interface Props {
  onFileDrag?: () => void;
  onFileDrop?: (files: File[]) => void;
  children?: (dragging: boolean) => Nullable<JSX.Element>;
  containerClassName?: string;
  dragZoneClassName?: string;
  removeBorder?: boolean;
  preventResetStyleOnFileDrop?: boolean;
}

const overwriteDefaultEventBehavior = <T,>(event: MouseEvent<T>) => {
  event.preventDefault();
  event.stopPropagation();
};

export const DropZoneComponent = ({
  children = () => null,
  containerClassName = 'h-full',
  dragZoneClassName = 'h-full',
  onFileDrop = functionNoop,
  onFileDrag = functionNoop,
  preventResetStyleOnFileDrop,
  removeBorder = true,
}: Props) => {
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const htmlElement = document.querySelector('html');
    if (htmlElement) {
      if (dragging) {
        htmlElement.classList.add('journey-dropzone-active');
      } else {
        htmlElement.classList.remove('journey-dropzone-active');
      }
    }
  }, [dragging]);

  const onFileDragEvent: DragEventHandler = (event) => {
    overwriteDefaultEventBehavior(event);
    if (event.type === 'dragenter' || event.type === 'dragover') {
      onFileDrag();
      setDragging(true);
    } else if (event.type === 'dragleave') {
      setDragging(false);
    }
  };

  const onFileDropped: DragEventHandler = (event) => {
    overwriteDefaultEventBehavior(event);

    const { dataTransfer } = event;

    if (!dataTransfer) {
      return;
    }
    onFileDrop(Array.from(dataTransfer.files));
    if (!preventResetStyleOnFileDrop) {
      setDragging(false);
    }
  };

  return (
    <div className='relative grow flex flex-col w-full'>
      <div
        className={classNames('relative flex gap-6', containerClassName)}
        onDragEnter={onFileDragEvent}
        onDrop={onFileDropped}
      >
        {children(dragging)}
        <div
          className={classNames(
            'absolute right-0 top-0 self-start w-full transition-opacity bg-transparent z-10 text-bedrock-black rounded-lg',
            {
              'border-dashed bg-bedrock-light-gray border-2': !removeBorder,
              'pointer-events-auto opacity-100': dragging,
              'pointer-events-none opacity-0': !dragging,
            },
            dragZoneClassName
          )}
        />
      </div>
      {dragging && (
        <div
          onDragOver={onFileDragEvent}
          onDragLeave={onFileDragEvent}
          onDrop={onFileDropped}
          className='absolute right-0 top-0 z-20 bg-transparent w-full h-full'
        />
      )}
    </div>
  );
};
